<script setup lang="ts">
import {
  getCategoryRoute,
  getTranslatedProperty,
} from "@shopware-pwa/helpers-next";
import type { Schemas } from "#shopware";
import { onKeyStroke, useActiveElement } from "@vueuse/core";

type NavigationElement = Schemas["Category"] & {
  activeClass?: boolean;
};

const { navigationElements } = useNavigation();
const currentMenuPosition = ref<string | undefined>(undefined);
const resetActiveClass = ref<boolean>(true);
const isMenuOpen = ref(false);
const openChildMenus = ref<string[]>([]);

const emit = defineEmits({
  close: () => {
    return true;
  },
  toggleMenu: () => true,
});

const route = useRoute();
const localePath = useLocalePath();
const { formatLink } = useInternationalization(localePath);

// New function to find active parent
const findActiveParent = (navigation: NavigationElement[]): string | null => {
  for (const element of navigation) {
    if (isActive(element)) {
      return element.id;
    }
    if (element.children && element.children.length > 0) {
      for (const child of element.children) {
        if (isActive(child)) {
          return element.id;
        }
      }
    }
  }
  return null;
};

// Modified onMounted to include opening active submenu
onMounted(() => {
  let currentNaviagtionElement: NavigationElement | undefined;
  if (navigationElements.value) {
    currentNaviagtionElement = findNavigationElement(
      route.path.slice(1),
      navigationElements.value
    );

    // Open submenu of active parent
    const activeParentId = findActiveParent(navigationElements.value);
    if (activeParentId) {
      openChildMenus.value = [activeParentId];
    }
  }
  if (currentNaviagtionElement) {
    updateActiveClass(
      currentNaviagtionElement.id,
      currentNaviagtionElement.parentId
    );
  }

  setMenuSize();
  window.addEventListener("resize", setMenuSize);
});

const findNavigationElement = (
  routePath: string,
  navigation: NavigationElement[]
): NavigationElement | undefined => {
  for (let i = 0; i < navigation.length; i++) {
    const navigationElement = navigation[i];
    const seoUrls = navigationElement.seoUrls as
      | Schemas["SeoUrl"][]
      | undefined;
    if (seoUrls) {
      for (let j = 0; j < seoUrls.length; j++) {
        const currentSeoUrl = seoUrls[j];
        if (currentSeoUrl && currentSeoUrl.seoPathInfo === routePath) {
          return navigationElement;
        }
      }
    }
    const children = navigationElement.children;
    if (children) {
      const foundElement = findNavigationElement(routePath, children);
      if (foundElement) {
        return foundElement;
      }
    }
  }
  return undefined;
};

const onUpdateActiveClass = (
  navigationId: string,
  parentId: string | undefined
) => {
  updateActiveClass(navigationId, parentId);
};

const resetNavigationActiveClass = (navigation: NavigationElement[]) => {
  for (let ni = 0; ni < navigation.length; ++ni) {
    navigation[ni].activeClass = false;
    const children = navigation[ni].children;
    if (children) {
      resetNavigationActiveClass(children);
    }
  }
};

const updateActiveClass = (
  navigationId: string,
  parentId: string | undefined
) => {
  const setNavigationActiveClass = (
    navigation: NavigationElement[],
    navigationId: string,
    parentId: string | undefined
  ) => {
    for (let ni = 0; ni < navigation.length; ++ni) {
      if (navigation[ni].id === navigationId) {
        navigation[ni].activeClass = true;
      }
      if (navigation[ni].id == parentId) {
        navigation[ni].activeClass = true;
        if (navigationElements.value) {
          setNavigationActiveClass(
            navigationElements.value,
            navigationId,
            navigation[ni].parentId
          );
        }
      }
      const children = navigation[ni].children;
      if (children) {
        setNavigationActiveClass(children, navigationId, parentId);
      }
    }
  };

  if (navigationElements.value) {
    resetNavigationActiveClass(navigationElements.value);
    setNavigationActiveClass(navigationElements.value, navigationId, parentId);
    resetActiveClass.value = false;
  }
};

const activeElement = useActiveElement();
onKeyStroke("ArrowDown", (e) => {
  if (
    activeElement.value?.parentElement?.nextElementSibling?.getElementsByTagName(
      "a"
    )[0]
  ) {
    e.preventDefault();
    activeElement.value.parentElement.nextElementSibling
      .getElementsByTagName("a")[0]
      .focus();
    return;
  }
});

onKeyStroke("ArrowUp", (e) => {
  if (
    activeElement.value?.parentElement?.previousElementSibling?.getElementsByTagName(
      "a"
    )[0]
  ) {
    e.preventDefault();
    activeElement.value?.parentElement.previousElementSibling
      .getElementsByTagName("a")[0]
      .focus();
    return;
  }
});

// Modified watch to handle active submenu
watch(
  () => route.path,
  () => {
    if (resetActiveClass.value == true && navigationElements.value) {
      resetNavigationActiveClass(navigationElements.value);
    }
    resetActiveClass.value = true;
    currentMenuPosition.value = undefined;

    // Find and open active parent's submenu
    if (navigationElements.value) {
      const activeParentId = findActiveParent(navigationElements.value);
      if (activeParentId) {
        openChildMenus.value = [activeParentId];
      } else {
        openChildMenus.value = [];
      }
    }

    emit("close");
  }
);

const setMenuSize = () => {
  const menuContainer = document.querySelector(".main-menu .container");
  const spanBefore = document.querySelectorAll(".main-menu-bg .span-before");
  const spanAfter = document.querySelectorAll(".main-menu-bg .span-after");

  if (menuContainer && spanBefore && spanAfter) {
    const menuSize = (window.innerWidth - menuContainer.clientWidth) / 2 + 15;

    for (var i = 0; i < spanBefore.length; i++) {
      spanBefore[i].style.width = menuSize + "px";
      spanAfter[i].style.width = menuSize + "px";
    }
  }
};

onBeforeUnmount(() => {
  window.removeEventListener("resize", setMenuSize);
});

const toggleMenu = () => {
  isMenuOpen.value = !isMenuOpen.value;
  if (!isMenuOpen.value) {
    if (navigationElements.value) {
      const activeParentId = findActiveParent(navigationElements.value);
      if (activeParentId) {
        openChildMenus.value = [activeParentId];
      } else {
        openChildMenus.value = [];
      }
    }
  }
};

const handleLinkClick = () => {
  toggleMenu(); // Close the menu
};

const toggleChildMenu = (navigationElement: NavigationElement) => {
  const index = openChildMenus.value.indexOf(navigationElement.id);
  if (index > -1) {
    openChildMenus.value.splice(index, 1);
  } else {
    openChildMenus.value = [navigationElement.id];
  }
};

const isChildMenuOpen = (navigationElement: NavigationElement) => {
  return openChildMenus.value.includes(navigationElement.id);
};

const isActive = (navigationElement: NavigationElement) => {
  const categoryRoute = formatLink(getCategoryRoute(navigationElement));
  if (
    categoryRoute &&
    typeof categoryRoute === "object" &&
    "path" in categoryRoute
  ) {
    return route.path === categoryRoute.path;
  }
  return false;
};

defineExpose({
  toggleMenu,
});
</script>

<template>
  <div
    id="mobileMenu"
    class="fixed inset-0 bg-white z-50 transform transition-transform lg:hidden h-full overflow-y-auto"
    :class="{ '-translate-x-full': !isMenuOpen }"
  >
    <div class="container mx-auto px-8 py-4 relative">
      <button
        id="closeMobileMenu"
        class="text-black bg-transparent border-none absolute top-6 right-6 text-2xl"
        @click="toggleMenu"
      >
        <i class="fa-solid fa-xmark"></i>
      </button>
      <ul class="mt-4 space-y-4">
        <li
          v-for="(navigationElement, index) in navigationElements"
          :key="navigationElement.id"
          @mouseover="currentMenuPosition = navigationElement.id"
          @focusin="currentMenuPosition = navigationElement.id"
          :class="{
            'relative group': navigationElement.children.length > 0,
          }"
        >
          <NuxtLink
            v-if="navigationElement.children.length === 0"
            :to="formatLink(getCategoryRoute(navigationElement))"
            class="block text-xl font-bold hover:text-red-bg"
            :class="{
              'text-red-bg': isActive(navigationElement),
              'text-dark-bg': !isActive(navigationElement),
            }"
            @click="handleLinkClick"
          >
            {{ navigationElement.translated.name }}
          </NuxtLink>
          <span
            v-else
            :to="formatLink(getCategoryRoute(navigationElement))"
            class="block text-xl font-bold hover:text-red-bg cursor-pointer"
            :class="{
              'text-red-bg':
                isActive(navigationElement) ||
                isChildMenuOpen(navigationElement),
              'text-dark-bg':
                !isActive(navigationElement) &&
                !isChildMenuOpen(navigationElement),
            }"
          >
            <NuxtLink
              :to="formatLink(getCategoryRoute(navigationElement))"
              @click="handleLinkClick"
            >
              {{ navigationElement.translated.name }}
            </NuxtLink>
            <i
              class="fa-solid transition-duration-300 fa-chevron-down ml-1 text-lg h-8 w-8 !inline-flex items-center justify-center"
              :class="{ 'rotate-180': isChildMenuOpen(navigationElement) }"
              @click.prevent="toggleChildMenu(navigationElement)"
            ></i>
          </span>
          <ul class="ml-0 mt-2" v-if="isChildMenuOpen(navigationElement)">
            <li v-for="child in navigationElement.children" :key="child.id">
              <NuxtLink
                :to="formatLink(getCategoryRoute(child))"
                class="block px-4 py-2 hover:text-red-bg"
                :class="{
                  'text-red-bg': isActive(child),
                  'text-dark-bg': !isActive(child),
                }"
                @click="handleLinkClick"
              >
                {{ child.translated.name }}
              </NuxtLink>
            </li>
          </ul>
        </li>
      </ul>
    </div>
  </div>
</template>

<style scoped></style>
